import { Box, Grid, Typography } from '@mui/material';
import { EAssetType } from 'constants/enums';
import { UploadsTooltips } from 'hooks/useUpload';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';
import { Flex } from 'components/ui/flex';

import { EToasterStatus, showToast } from '../../../../utils/showToast';
import { validateImageRatio } from '../../utils';

import { CheckoutLegalText } from '../CheckoutLegalText';
import 'style/forms.scss';
import usePermissions from 'api/usePermissions';
import { useParams } from 'react-router-dom';
import Logo from './CommonFields/Logo';
import PreviewButton from './CommonFields/PreviewButton';
import { CheckoutThemeProps } from '../types';

const CheckoutThemeV2 = ({
  defaultValues,
  pictures,
  setFieldValue,
  handleChange,
  touched,
  values,
  handleBlur,
  setShowPreview,
  errors
}: CheckoutThemeProps) => {
  const { isSuperAdminByProject } = usePermissions();
  const { publisherId } = useParams();

  return (
    <Flex className="formContent w-full">
      <Flex direction="row" justify="between">
        <Grid container>
          <Grid item xs={5}>
            <Typography
              variant="h6"
              sx={{
                marginBottom: '12px',
                fontFamily: 'Inter, Montserrat, serif',
                fontWeight: '600'
              }}
            >
              Checkout Page
            </Typography>
            <Flex className="gap-6">
              <AcCard stackContainer={false}>
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <Logo
                      defaultValues={defaultValues}
                      pictures={pictures}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AcSolidInput
                      header="Primary Color"
                      name="primaryColor"
                      defaultValue={defaultValues.primaryColor}
                      setValue={setFieldValue}
                      onBlur={handleBlur}
                      dataTestId="checkoutPrimaryColor"
                      dataTestPopoverId="checkoutPrimaryColorPopover"
                      dataTestColorPickerId="checkoutPrimaryColorPicker"
                      tooltip="This will be the primary color of your checkout. It will appear behind the background image in case no background image is present."
                      error={
                        touched.primaryColor && Boolean(errors.primaryColor)
                      }
                      helperText={
                        touched.primaryColor
                          ? errors.primaryColor?.toString()
                          : ''
                      }
                    />
                    <Box mt={3}>
                      <AcSolidInput
                        header="Font Color"
                        name="textColor"
                        defaultValue={defaultValues.textColor}
                        setValue={setFieldValue}
                        onBlur={handleBlur}
                        dataTestId="checkoutTextColor"
                        dataTestPopoverId="checkoutTextColorPopover"
                        dataTestColorPickerId="checkoutTextColorPicker"
                        error={touched.textColor && Boolean(errors.textColor)}
                        helperText={
                          touched.textColor ? errors.textColor?.toString() : ''
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Background Image"
                      name="backgroundImage"
                      value={defaultValues.backgroundImage}
                      dataTestBoxId="checkoutBackgroundImage"
                      uploadBtnTestId="uploadCheckoutBackgroundImage"
                      dataTestListId="checkoutBackgroundImageList"
                      optionDataTestIdPrefix="checkoutBackgroundImageOption"
                      onClear={() => {
                        setFieldValue('checkout.backgroundImage', '');
                      }}
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('checkout.backgroundImage', url);
                        },
                        uploadType: EAssetType.BG_DESKTOP,
                        validator: async (file) => {
                          const isValid = await validateImageRatio(file, 1 / 2);
                          if (!isValid) {
                            showToast({
                              message: 'Image aspect ratio must be 1:2',
                              status: EToasterStatus.ERROR
                            });
                          }
                          return isValid;
                        },
                        uploadMessage: UploadsTooltips.BG_CHECKOUT
                      }}
                      items={[
                        {
                          content: 'Default Appcharge background',
                          key: 'https://media.appcharge.com/defaults/background.png',
                          value:
                            'https://media.appcharge.com/defaults/background.png',
                          renderFunction: () => {
                            return (
                              <Flex
                                direction="row"
                                align="center"
                                className="gap-2"
                              >
                                <img
                                  src="https://media.appcharge.com/defaults/background.png"
                                  alt="Default Appcharge background"
                                  style={{
                                    width: 30,
                                    maxHeight: 30
                                  }}
                                />
                                <Typography>
                                  Default Appcharge background
                                </Typography>
                              </Flex>
                            );
                          }
                        },
                        ...(pictures.data || [])
                          .filter((p: any) => p.type === EAssetType.BG_DESKTOP)
                          .map((picture: any) => {
                            return {
                              content: picture.name,
                              key: picture.url,
                              value: picture.url,
                              renderFunction: () => {
                                return (
                                  <Flex
                                    direction="row"
                                    align="center"
                                    className="gap-2"
                                  >
                                    <img
                                      src={picture.url}
                                      alt={picture.name}
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>{picture.name}</Typography>
                                  </Flex>
                                );
                              }
                            };
                          })
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.backgroundImage &&
                        Boolean(errors.backgroundImage)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Background Image (Mobile)"
                      name="mobileBgImage"
                      displayEmpty
                      value={
                        defaultValues.mobileBgImage ||
                        'No Background (Taken from Desktop)'
                      }
                      dataTestBoxId="checkoutMobileBgImage"
                      uploadBtnTestId="uploadCheckoutMobileBgImage"
                      dataTestListId="checkoutMobileBgImageList"
                      optionDataTestIdPrefix="checkoutMobileBgImageOption"
                      onClear={() => {
                        setFieldValue('checkout.mobileBgImage', '');
                      }}
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('checkout.mobileBgImage', url);
                        },
                        uploadType: EAssetType.BG_MOBILE,
                        validator: async (file) => {
                          const isValid = await validateImageRatio(file, 1 / 2);
                          if (!isValid) {
                            showToast({
                              message: 'Image aspect ratio must be 1:2',
                              status: EToasterStatus.ERROR
                            });
                          }
                          return isValid;
                        },
                        uploadMessage: UploadsTooltips.BG_CHECKOUT
                      }}
                      items={[
                        {
                          content: 'No Background (Taken from Desktop)',
                          key: '',
                          value: '',
                          renderFunction: () => {
                            return (
                              <Flex
                                direction="row"
                                align="center"
                                className="gap-2"
                              >
                                <Typography>
                                  No Background (Taken from Desktop)
                                </Typography>
                              </Flex>
                            );
                          }
                        },
                        ...(pictures.data || [])
                          .filter((p: any) => p.type === EAssetType.BG_MOBILE)
                          .map((picture: any) => {
                            return {
                              content: picture.name,
                              key: picture.url,
                              value: picture.url,
                              renderFunction: () => {
                                return (
                                  <Flex
                                    direction="row"
                                    align="center"
                                    className="gap-2"
                                  >
                                    <img
                                      src={picture.url}
                                      alt={picture.name}
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>{picture.name}</Typography>
                                  </Flex>
                                );
                              }
                            };
                          })
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.mobileBgImage && Boolean(errors.mobileBgImage)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} mt={0}>
                    <AcSolidInput
                      header="Button Color"
                      name="buttonColor"
                      defaultValue={defaultValues.buttonColor}
                      setValue={setFieldValue}
                      onBlur={handleBlur}
                      dataTestId="checkoutButtonColor"
                      dataTestPopoverId="checkoutButtonColorPopover"
                      dataTestColorPickerId="checkoutButtonColorPicker"
                      error={touched.buttonColor && Boolean(errors.buttonColor)}
                      helperText={
                        touched.buttonColor
                          ? errors.buttonColor?.toString()
                          : ''
                      }
                    />
                    <Box mt={3}>
                      <AcSolidInput
                        header="Button Font Color"
                        name="buttonTextColor"
                        defaultValue={defaultValues.buttonTextColor}
                        setValue={setFieldValue}
                        onBlur={handleBlur}
                        dataTestId="checkoutButtonTextColor"
                        dataTestPopoverId="checkoutButtonTextColorPopover"
                        dataTestColorPickerId="checkoutButtonTextColorPicker"
                        error={
                          touched.buttonTextColor &&
                          Boolean(errors.buttonTextColor)
                        }
                      />
                    </Box>
                  </Grid>
                  {isSuperAdminByProject(publisherId || '') && (
                    <Grid item xs={12}>
                      <CheckoutLegalText
                        values={values}
                        setFieldValue={setFieldValue}
                        defaultValues={defaultValues}
                      />
                    </Grid>
                  )}
                </Grid>
              </AcCard>
            </Flex>
            <Box mt={4}>
              <PreviewButton setShowPreview={setShowPreview} />
            </Box>
          </Grid>
        </Grid>
      </Flex>
    </Flex>
  );
};

export default CheckoutThemeV2;
